/**
 * Which way did this browser last sign in? (owner, 2026-10-04: a "Last used"
 * tag on the login page, variant A of `design/last-used-signin-mockup.html`.)
 *
 * Recorded ONLY where a sign-in has completed, never on a click, so a wrong
 * password or an abandoned Google screen leaves the previous answer standing:
 * - `password`: `loginAction`, after `signInWithPassword` succeeds;
 * - `link`: `confirmLoginAction`, after `verifyOtp` succeeds;
 * - `google`: `/auth/callback`, after the code exchange, and only when the new
 *   session says it came from an OAuth provider (`signInMethodFromAccessToken`,
 *   the token's `amr` claim). That route also redeems the email SIGNUP
 *   confirmation, which is not a Google sign-in. Read from the session rather
 *   than a `method=google` on the return URL (review, 2026-10-04): a changed
 *   return URL has to pass Supabase's redirect allow list, and one that fails
 *   it sends every Google sign-in to the Site URL instead of the callback.
 *
 * A first-party cookie readable by the page (not httpOnly): it holds one of
 * three words and nothing about the person, and the forms read it after
 * hydration so the prerendered auth pages stay static. Anyone sharing the
 * browser can see which method was used, which is the trade every product
 * showing this tag makes.
 */
export const LAST_SIGNIN_COOKIE = "et_last_signin";

export const SIGNIN_METHODS = ["google", "password", "link"] as const;
export type SignInMethod = (typeof SIGNIN_METHODS)[number];

export const LAST_SIGNIN_COOKIE_OPTIONS = {
  path: "/",
  maxAge: 60 * 60 * 24 * 365,
  sameSite: "lax",
  secure: process.env.NODE_ENV === "production",
  httpOnly: false,
} as const;

export function isSignInMethod(value: unknown): value is SignInMethod {
  return typeof value === "string" && (SIGNIN_METHODS as readonly string[]).includes(value);
}

/** The recorded method in a `document.cookie` string, or null. */
export function lastSignInFrom(cookieHeader: string | null | undefined): SignInMethod | null {
  if (!cookieHeader) return null;
  for (const part of cookieHeader.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() !== LAST_SIGNIN_COOKIE) continue;
    const value = part.slice(eq + 1).trim();
    return isSignInMethod(value) ? value : null;
  }
  return null;
}

/**
 * How a just-created session signed in, from its access token's `amr` claim
 * (authentication methods, newest by timestamp). `oauth` is the only OAuth
 * provider we offer, Google; anything else (`email/signup`, `magiclink`,
 * `recovery`, a malformed token) is null and records nothing. The token comes
 * straight from our own code exchange and only labels this browser, so it is
 * decoded, not verified.
 */
export function signInMethodFromAccessToken(token: string | null | undefined): SignInMethod | null {
  if (!token) return null;
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const json = Buffer.from(part.replace(/-/g, "+").replace(/_/g, "/"), "base64").toString("utf8");
    const amr = (JSON.parse(json) as { amr?: unknown }).amr;
    if (!Array.isArray(amr) || amr.length === 0) return null;
    const latest = [...amr]
      .filter((e): e is { method: string; timestamp?: number } => !!e && typeof (e as { method?: unknown }).method === "string")
      .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))[0];
    return latest?.method === "oauth" ? "google" : null;
  } catch {
    return null;
  }
}
